import axios from 'axios';

/**
 * Dev-only HTTP request counter, exposed as `window.__apiStats`.
 *
 * Step 0 of `docs/api-call-volume-audit.md`: before any of the de-duplication
 * work lands we need a way to say "this flow used to cost N requests". Counting
 * in the Network panel does not work here because 214 requests carry a fresh
 * `uuidv4()` query param, so no two rows group together.
 *
 * Usage from the console:
 *
 *   __apiStats.reset()        // start a flow
 *   ...exercise the flow...
 *   __apiStats.log()          // console.table, worst offender first
 *   __apiStats.snapshot()     // plain object, safe to JSON.stringify and diff
 *
 * Never loaded in production - the module short-circuits and installs nothing.
 */

const isEnabled = process.env.NODE_ENV !== 'production';

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const OBJECT_ID_RE = /\b[0-9a-f]{24}\b/gi;
const LONG_ID_RE = /\b\d{4,}\b/g;

/**
 * Collapses a request URL down to a stable key so that the same logical
 * endpoint groups together across calls.
 *
 * Drops the origin, the `request-id` / `requestId` cache-buster (both spellings
 * are in use - see audit section 4), and any id-shaped path segment.
 *
 * @param {string} url raw request url, absolute or relative
 * @returns {string} normalized path, e.g. `/us/deals/:id/stakeholders`
 */
const normalizeUrl = (url) => {
  if (!url) return '(unknown)';
  let path = String(url);
  try {
    // Absolute urls (ipify, the /log poster) parse; relative ones throw.
    path = new URL(path).pathname;
  } catch {
    path = path.split('?')[0];
  }
  return path
    .replace(UUID_RE, ':id')
    .replace(OBJECT_ID_RE, ':id')
    .replace(LONG_ID_RE, ':id');
};

const state = {
  total: 0,
  byKey: new Map(),
  startedAt: Date.now(),
};

/**
 * Records one outgoing request against its normalized key.
 *
 * @param {object} config axios request config
 * @returns {object} the same config, unmodified
 */
const record = (config) => {
  const method = (config?.method || 'get').toUpperCase();
  const key = `${method} ${normalizeUrl(config?.url)}`;
  state.total += 1;
  state.byKey.set(key, (state.byKey.get(key) || 0) + 1);
  return config;
};

/**
 * Installs the counter on the shared instance, on the bare `axios` default
 * (which `utils/logger.js` and the ipify call use), and on `window`.
 *
 * @param {import('axios').AxiosInstance} instance the app's shared instance
 * @returns {void}
 */
export const installApiStats = (instance) => {
  if (!isEnabled || typeof window === 'undefined') return;
  if (window.__apiStats) return;

  instance.interceptors.request.use(record);
  axios.interceptors.request.use(record);

  window.__apiStats = {
    /** @returns {number} requests counted since the last reset */
    get total() {
      return state.total;
    },
    /** Clears all counters and restarts the clock. */
    reset() {
      state.total = 0;
      state.byKey.clear();
      state.startedAt = Date.now();
    },
    /**
     * @returns {{total: number, elapsedMs: number, byKey: Object<string, number>}}
     *   a plain snapshot, ordered worst-first, safe to stringify and diff
     */
    snapshot() {
      const sorted = [...state.byKey.entries()].sort((a, b) => b[1] - a[1]);
      return {
        total: state.total,
        elapsedMs: Date.now() - state.startedAt,
        byKey: Object.fromEntries(sorted),
      };
    },
    /** Prints the snapshot as a table. */
    log() {
      const snap = this.snapshot();
      // eslint-disable-next-line no-console
      console.log(
        `__apiStats: ${snap.total} requests in ${Math.round(
          snap.elapsedMs / 1000,
        )}s`,
      );
      // eslint-disable-next-line no-console
      console.table(
        Object.entries(snap.byKey).map(([endpoint, count]) => ({
          endpoint,
          count,
        })),
      );
      return snap;
    },
  };
};

export default installApiStats;
